// Journal des actions (administrateur) : qui a fait quoi, quand, depuis où — filtres, recherche, export CSV.
import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Collapse, FormControlLabel, Grid, IconButton, InputAdornment, MenuItem, Switch, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import {
  DownloadOutlined, ExpandMore, GppMaybeOutlined, HistoryOutlined, LockOutlined, SearchOutlined,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { Kpi, Panel } from '../components/ui';
import { journalAPI } from '../services/api';
import { apiError, dateHeureFr } from '../utils/format';
import { tokens } from '../theme';

const PAGE = 100;
const Resultat = ({ s }) => (s < 300 ? <Chip size="small" label="OK" color="success" variant="outlined" />
  : s === 403 ? <Chip size="small" label="Refusé" color="warning" variant="outlined" />
    : <Chip size="small" label={s === 401 ? 'Refusé (identifiants)' : s === 429 ? 'Bloqué' : `Échec ${s}`} color="error" variant="outlined" />);

const Journal = () => {
  const [f, setF] = useState({ du: '', au: '', user_id: '', module: '', q: '', sensible: false, echecs: false, connexions: false });
  const [q, setQ] = useState('');
  const [d, setD] = useState(null);
  const [offset, setOffset] = useState(0);
  const [ouvert, setOuvert] = useState(null);
  const [error, setError] = useState('');
  const params = { ...f, q, sensible: f.sensible ? 1 : undefined, echecs: f.echecs ? 1 : undefined, connexions: f.connexions ? 1 : undefined };
  Object.keys(params).forEach((k) => (params[k] === '' || params[k] === undefined) && delete params[k]);

  useEffect(() => { const t = setTimeout(() => setQ(f.q), 300); return () => clearTimeout(t); }, [f.q]);
  useEffect(() => { setOffset(0); }, [f.du, f.au, f.user_id, f.module, q, f.sensible, f.echecs, f.connexions]);
  useEffect(() => {
    journalAPI.list({ ...params, limit: PAGE, offset }).then((r) => setD(r.data)).catch((e) => setError(apiError(e)));
  }, [f.du, f.au, f.user_id, f.module, q, f.sensible, f.echecs, f.connexions, offset]); // eslint-disable-line

  const exporter = async () => {
    try {
      const r = await journalAPI.csv(params);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `journal-docc-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { setError(apiError(e)); }
  };
  const set = (patch) => setF((x) => ({ ...x, ...patch }));
  const r = d?.resume;

  return (
    <Layout title="Journal des actions" plain actions={<Button startIcon={<DownloadOutlined />} onClick={exporter}>Exporter (CSV)</Button>}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {r && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}><Kpi compact icon={HistoryOutlined} tint="navy" label="Actions aujourd'hui" value={r.aujourdhui} /></Grid>
          <Grid item xs={6} sm={4}><Kpi compact icon={GppMaybeOutlined} tint="amber" label="Actions sensibles" value={r.sensibles_7j} sub="7 derniers jours" onClick={() => set({ sensible: true })} /></Grid>
          <Grid item xs={6} sm={4}><Kpi compact icon={LockOutlined} tint="red" label="Échecs de connexion" value={r.echecs_connexion_7j} sub="7 derniers jours"
            valueColor={r.echecs_connexion_7j >= 10 ? '#B4232A' : undefined} onClick={() => set({ connexions: true, echecs: true })} /></Grid>
        </Grid>
      )}
      <Panel flush>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <TextField size="small" placeholder="Action, utilisateur, n° de pièce, IP…" value={f.q} onChange={(e) => set({ q: e.target.value })} sx={{ minWidth: 240, flex: '1 1 240px' }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined fontSize="small" /></InputAdornment> }} />
          <TextField size="small" type="date" label="Du" InputLabelProps={{ shrink: true }} value={f.du} onChange={(e) => set({ du: e.target.value })} />
          <TextField size="small" type="date" label="Au" InputLabelProps={{ shrink: true }} value={f.au} onChange={(e) => set({ au: e.target.value })} />
          <TextField select size="small" label="Utilisateur" value={f.user_id} onChange={(e) => set({ user_id: e.target.value })} sx={{ minWidth: 170 }}>
            <MenuItem value="">Tous</MenuItem>
            {(d?.users || []).map((u) => <MenuItem key={u.user_id} value={u.user_id}>{u.nom ? `${u.nom} (${u.username})` : u.username}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Module" value={f.module} onChange={(e) => set({ module: e.target.value })} sx={{ minWidth: 160 }}>
            <MenuItem value="">Tous</MenuItem>
            {(d?.modules || []).map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
          </TextField>
          <FormControlLabel control={<Switch size="small" checked={f.sensible} onChange={(e) => set({ sensible: e.target.checked })} />} label="Sensibles" />
          <FormControlLabel control={<Switch size="small" checked={f.echecs} onChange={(e) => set({ echecs: e.target.checked })} />} label="Échecs / refus" />
          <FormControlLabel control={<Switch size="small" checked={f.connexions} onChange={(e) => set({ connexions: e.target.checked })} />} label="Connexions" />
        </Box>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow><TableCell sx={{ width: 36 }} /><TableCell>Date</TableCell><TableCell>Utilisateur</TableCell><TableCell>Action</TableCell>
                <TableCell>Détail</TableCell><TableCell>Résultat</TableCell><TableCell>IP</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {d?.entrees.map((j) => (
                <React.Fragment key={j.id}>
                  <TableRow hover sx={{ cursor: 'pointer', '& td': { borderBottom: ouvert === j.id ? 0 : undefined } }} onClick={() => setOuvert(ouvert === j.id ? null : j.id)}>
                    <TableCell><IconButton size="small" aria-label="Détails"><ExpandMore fontSize="small" sx={{ transform: ouvert === j.id ? 'rotate(180deg)' : 'none' }} /></IconButton></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 13 }}>{dateHeureFr(j.created_at)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{j.nom || j.username || '—'}</Typography>
                      {j.nom && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{j.username}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {j.sensible && <GppMaybeOutlined sx={{ fontSize: 17, color: '#B7791F' }} titleAccess="Action sensible" />}
                        <Typography sx={{ fontSize: 14 }}>{j.action}</Typography>
                      </Box>
                      {j.module && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{j.module}</Typography>}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, maxWidth: 380 }}>{j.detail || '—'}</TableCell>
                    <TableCell><Resultat s={j.statut} /></TableCell>
                    <TableCell sx={{ fontFamily: tokens.mono, fontSize: 12.5, whiteSpace: 'nowrap' }}>{j.ip || '—'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={7} sx={{ p: 0, borderBottom: ouvert === j.id ? undefined : 0 }}>
                      <Collapse in={ouvert === j.id} unmountOnExit>
                        <Box sx={{ px: 2, py: 1.5, bgcolor: '#F7F8FA', fontSize: 13 }}>
                          <div><b>Requête :</b> <span style={{ fontFamily: tokens.mono }}>{j.methode} {j.chemin}</span> — code {j.statut}</div>
                          {j.donnees && (
                            <Box component="pre" sx={{ m: 0, mt: 1, p: 1, bgcolor: '#fff', border: `1px solid ${tokens.line}`, borderRadius: 1, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 260, overflow: 'auto' }}>
                              {JSON.stringify(j.donnees, null, 2)}
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
              {d && !d.entrees.length && <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>Aucune action ne correspond.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
        {d && d.total > PAGE && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5 }}>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{offset + 1}–{Math.min(offset + PAGE, d.total)} sur {d.total}</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" disabled={!offset} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Plus récentes</Button>
              <Button size="small" disabled={offset + PAGE >= d.total} onClick={() => setOffset(offset + PAGE)}>Plus anciennes</Button>
            </Box>
          </Box>
        )}
      </Panel>
      <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1.5 }}>
        Toute modification de données est enregistrée (création, modification, suppression, émission, paiement, accès…), ainsi que chaque tentative de connexion.
        Les mots de passe ne sont jamais enregistrés. Le journal ne peut être ni modifié ni effacé, y compris lors d'une remise à zéro des données.
      </Typography>
    </Layout>
  );
};

export default Journal;
